import { IQueryCommandOptions, convertQueryCommandToQueryString, IWhereCommand, In } from '@llws/dynamic-router-utils';
import { adminRequest } from '@/shared/request-configs/admin-request';
import {
  TPostType,
  IGenericObject,
  mergeQueryCommandOptionsV2,
} from '@/shared';
import tags from './tags';

export const postTypeEndpoints: any = {
  Bios: '/bios',
  Blog: '/blogs',
  Careers: '/careers_postings',
  Documents: '/documents',
  FAQs: '/faqs',
  News: '/news',
  Testimonials: '/testimonials',
};

export const tagModels: Record<string, string> = {
  Blog: 'blogs',
  Careers: 'career',
  FAQs: 'faqs',
  News: 'news',
};

/**
 * Fetches Posts by Site ID <br>
 * API Endpoint - `/website-api/posts${convertCommandToQueryString(mergedFilters)}` <br> via {@link adminRequest.get}
 * @param {number} siteId - id of the site
 * @param {QueryStringCommandOptions} [filters] - optional query string filters
 */
export const get = (
  siteId: number,
  filters?: IQueryCommandOptions,
) => {
  if (filters && filters.where) {
    const statusIdx = (filters.where).findIndex((v) => (v as any).field === 'status');

    if (statusIdx !== -1) {
      if ((filters.where[statusIdx] as IWhereCommand).value === 'published') {
        // eslint-disable-next-line no-param-reassign
        (filters.where[statusIdx] as IWhereCommand) = In('status', ['published', 'enabled']);
      } else if ((filters.where[statusIdx] as IWhereCommand).value === 'private') {
        // eslint-disable-next-line no-param-reassign
        (filters.where[statusIdx] as IWhereCommand) = In('status', ['private', 'disabled', 'hidden']);
      }
    }
  }

  const mergedFilters: IQueryCommandOptions = mergeQueryCommandOptionsV2([
    filters || {},
    {
      where: [
        {
          field: 'siteId',
          value: siteId.toString(),
        },
        {
          field: 'status',
          notOperator: true,
          value: 'deleted',
        },
      ],
    },
  ]);

  const postTypeClauseIndex = mergedFilters.where?.findIndex((w) => (w as IWhereCommand).field === 'postType');

  if (postTypeClauseIndex && postTypeClauseIndex !== -1) {
    const { value } = (mergedFilters.where![postTypeClauseIndex] as IWhereCommand)!;
    (mergedFilters.where![postTypeClauseIndex] as IWhereCommand) = In((
      mergedFilters.where![postTypeClauseIndex] as IWhereCommand).field, Array.isArray(value) ? value : [value]);
  }
  return adminRequest.get(`/website-api/posts${convertQueryCommandToQueryString(mergedFilters)}`);
};

/**
 * Fetches Posts by Site & Type <br>
 * API Endpoint - `/website-api/sites/${siteId}${postTypeEndpoints[postType]}${convertCommandToQueryString(mergedFilters)}` <br> via {@link adminRequest.get}
 * @param {number} siteId - id of the site
 * @param {TPostType} postType - type of the post
 * @param {QueryStringCommandOptions} [filters] - optional query string filters
 */
export const getByType = (
  siteId: number,
  postType: TPostType,
  filters?: IQueryCommandOptions,
) => {
  if (filters && filters.where) {
    const statusIdx = (filters.where).findIndex((v) => (v as any).field === 'status');

    if (statusIdx !== -1) {
      if ((filters.where[statusIdx] as IWhereCommand).value === 'published') {
        // eslint-disable-next-line no-param-reassign
        (filters.where[statusIdx] as IWhereCommand) = In('status', ['published', 'enabled']);
      } else if ((filters.where[statusIdx] as IWhereCommand).value === 'private') {
        // eslint-disable-next-line no-param-reassign
        (filters.where[statusIdx] as IWhereCommand) = In('status', ['private', 'disabled', 'hidden']);
      }
    }
  }

  const mergedFilters = mergeQueryCommandOptionsV2([
    filters || {},
    {
      where: [
        {
          field: 'siteId',
          value: siteId.toString(),
        },
        {
          field: 'status',
          notOperator: true,
          value: 'deleted',
        },
      ],
    },
  ]);
  return adminRequest.get(
    `/website-api/sites/${siteId}${postTypeEndpoints[postType]}${convertQueryCommandToQueryString(mergedFilters)}`,
  );
};
/**
 * Deletes Post <br>
 * API Endpoint - `/website-api/sites/${siteId}${postTypeEndpoints[postType]}` <br> via {@link adminRequest.delete}
 * @param {number} id - id of the post to remove
 * @param {number} siteId - id of the site
 * @param {TPostType} postType - type of the post
 */
export const removePost = (
  id: number,
  siteId: number,
  postType: TPostType,
) => adminRequest.delete(`/website-api/sites/${siteId}${postTypeEndpoints[postType]}`, [id]);
/**
 * Fetches Single Post by Type <br>
 * API Endpoint - `/website-api/sites/${siteId}${postTypeEndpoints[postType]}/${id}` <br> via {@link adminRequest.get}
 * @param {number} id - id of the post
 * @param {number} siteId - id of the site
 * @param {TPostType} postType - type of the post
 */
export const getSingleByType = async (
  id: number,
  siteId: number,
  postType: TPostType,
) => {
  const post = await adminRequest.get(`/website-api/sites/${siteId}${postTypeEndpoints[postType]}/${id}`);
  if (tagModels[postType]) {
    const postTags = await tags.getByModelAndObjectId(tagModels[postType], id, false);
    post.data!.tags = postTags.data || [];
  }
  return post;
};
/**
 * Updates Single Post By Type <br>
 * API Endpoint - `/website-api/sites/${siteId}${postTypeEndpoints[postType]}/${id}` <br> via {@link adminRequest.put}
 * @param {number} id - id of the post
 * @param {number} siteId - id of the site
 * @param {TPostType} postType - type of the post
 * @param {IGenericObject} data - payload of updated post
 */
export const updateSingleByType = (
  id: number,
  siteId: number,
  postType: TPostType,
  data: IGenericObject,
) => adminRequest.put(`/website-api/sites/${siteId}${postTypeEndpoints[postType]}/${id}`, data);
/**
 * Updates Posts by Type <br>
 * API Endpoint - `/website-api/sites/${siteId}${postTypeEndpoints[postType]}` <br> via {@link adminRequest.put}
 * @param {number} siteId - id of the site
 * @param {TPostType} postType - type of the post
 * @param {IGenericObject} data - payload of updated posts
 */
export const updateByType = (
  siteId: number,
  postType: TPostType,
  data: IGenericObject,
) => adminRequest.put(`/website-api/sites/${siteId}${postTypeEndpoints[postType]}`, data);
/**
 * Creates New Post by Type <br>
 * API Endpoint - `/website-api/sites/${siteId}${postTypeEndpoints[postType]}` <br> via {@link adminRequest.post}
 * @param {number} siteId - id of the site
 * @param {TPostType} postType - type of the post
 * @param {IGenericObject} data - payload of new post
 */
export const insertSingleByType = (
  siteId: number,
  postType: TPostType,
  data: IGenericObject,
) => adminRequest.post(`/website-api/sites/${siteId}${postTypeEndpoints[postType]}`, data);

/**
 * @param postType String to identify the title of each post
*/
export const getPostField = (postType: string): string => {
  const postFieldMap: Record<string, string> = {
    bios: 'name',
    testimonials: 'name',
    faqs: 'question',
    publishdate: 'publishDate',
  };
  return postFieldMap[postType.toLowerCase()] ?? 'title';
};

export default {
  postTypeEndpoints,
  get,
  getByType,
  getSingleByType,
  updateSingleByType,
  updateByType,
  insertSingleByType,
  removePost,
  getPostField,
};
